<template>
  <div class="dsd-item d-flex flex-column">
    <h2 :title="$t('message.tooltip.datasetDetails.distribution')" class="mb-lg-4" data-placement="top"
      data-cy="dataset-distributions">
      {{ $t('message.metadata.distributions') }} <span class="count-badge">{{ getDistributions ?
        getDistributions.length.toLocaleString('fi') : 0
      }} </span>
    </h2>

    <!-- Mobile Sort Controls -->
    <div class="mobile-sort-controls d-md-none mb-3">
      <button class="sort-button" @click="toggleSort('title')"
        :aria-label="$t('message.metadata.title') + ' sortieren'">
        Sort
        <div class="sort-arrows">
          <svg class="sort-arrow sort-arrow-up" :class="{ active: sortOrder === 'asc' }" width="10" height="8"
            viewBox="0 0 10 8">
            <path d="M5 1 L9 6 L1 6 Z" fill="currentColor" />
          </svg>
          <svg class="sort-arrow sort-arrow-down" :class="{ active: sortOrder === 'desc' }" width="10" height="8"
            viewBox="0 0 10 8">
            <path d="M5 7 L1 2 L9 2 Z" fill="currentColor" />
          </svg>
        </div>
      </button>
    </div>

    <!-- Desktop Table View -->
    <div class="table-responsive-wrapper d-none d-md-block">
      <div class="distributions-wrapper">
        <div class="distributions-grid" :class="{ 'has-rating': useEndorsements }">
          <!-- Header -->
          <div class="grid-header">
            <div class="grid-col title-col">
              <button class="sort-button" @click="toggleSort('title')"
                :aria-label="$t('message.metadata.title') + ' sort'">
                {{ $t('message.metadata.title') }}
                <div class="sort-arrows">
                  <svg class="sort-arrow sort-arrow-up" :class="{ active: sortOrder === 'asc' }" width="10" height="8"
                    viewBox="0 0 10 8">
                    <path d="M5 1 L9 6 L1 6 Z" fill="currentColor" />
                  </svg>
                  <svg class="sort-arrow sort-arrow-down" :class="{ active: sortOrder === 'desc' }" width="10"
                    height="8" viewBox="0 0 10 8">
                    <path d="M5 7 L1 2 L9 2 Z" fill="currentColor" />
                  </svg>
                </div>
              </button>
            </div>
            <div class="grid-col format-col">
              <button class="sort-button" @click="toggleSort('format')"
                :aria-label="$t('message.metadata.format') + ' sort'">
                {{ $t('message.metadata.format') }}
                <div class="sort-arrows">
                  <svg class="sort-arrow sort-arrow-up" :class="{ active: sortOrder === 'asc' }" width="10" height="8"
                    viewBox="0 0 10 8">
                    <path d="M5 1 L9 6 L1 6 Z" fill="currentColor" />
                  </svg>
                  <svg class="sort-arrow sort-arrow-down" :class="{ active: sortOrder === 'desc' }" width="10"
                    height="8" viewBox="0 0 10 8">
                    <path d="M5 7 L1 2 L9 2 Z" fill="currentColor" />
                  </svg>
                </div>
              </button>
            </div>
            <div class="grid-col date-col">
              <button class="sort-button" @click="toggleSort('format')"
                :aria-label="$t('message.metadata.format') + ' sort'">
                {{ $t('message.metadata.updated') }}
                <div class="sort-arrows">
                  <svg class="sort-arrow sort-arrow-up" :class="{ active: sortOrder === 'asc' }" width="10" height="8"
                    viewBox="0 0 10 8">
                    <path d="M5 1 L9 6 L1 6 Z" fill="currentColor" />
                  </svg>
                  <svg class="sort-arrow sort-arrow-down" :class="{ active: sortOrder === 'desc' }" width="10"
                    height="8" viewBox="0 0 10 8">
                    <path d="M5 7 L1 2 L9 2 Z" fill="currentColor" />
                  </svg>
                </div>
              </button>
            </div>
            <div class="grid-col actions-col">{{ $t('message.dataupload.menu.actions') }}</div>
            <div v-if="useEndorsements" class="grid-col actions-col">
              <button class="sort-button" @click="toggleSort('likes')"
                :aria-label="$t('message.datasetDetails.likes') + ' sort'">
                {{ $t('message.datasetDetails.likes') }}
                <div class="sort-arrows">
                  <svg class="sort-arrow sort-arrow-up" :class="{ active: sortOrder === 'asc' }" width="10" height="8"
                    viewBox="0 0 10 8">
                    <path d="M5 1 L9 6 L1 6 Z" fill="currentColor" />
                  </svg>
                  <svg class="sort-arrow sort-arrow-down" :class="{ active: sortOrder === 'desc' }" width="10"
                    height="8" viewBox="0 0 10 8">
                    <path d="M5 7 L1 2 L9 2 Z" fill="currentColor" />
                  </svg>
                </div>
              </button>
            </div>
          </div>
          <!-- Distribution rows -->
          <div v-for="(distribution, index) in visibleDistributions" :key="distribution.id" class="distribution-item"
            :class="{ 'accordion-open': expandedItems[distribution.id] }">
            <div class="grid-row distribution-header">
              <div class="grid-col title-col">
                <div class="title-with-arrow">
                  <button :id="`accordion-button-${index}`" class="accordion-title"
                    :aria-expanded="expandedItems[distribution.id] || false"
                    :aria-controls="`accordion-content-${index}`" type="button"
                    @click="toggleAccordion(distribution.id)" @keydown.enter="toggleAccordion(distribution.id)"
                    @keydown.space.prevent="toggleAccordion(distribution.id)">
                    <h3 class="m-0 distribution-title">{{ getDistributionTitle(distribution) }}</h3>
                    <svg :class="`accordion-arrow ${expandedItems[index] ? 'rotated' : ''}`" width="16" height="16"
                      viewBox="0 0 16 16" aria-hidden="true">
                      <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="2" fill="none" />
                    </svg>
                  </button>
                </div>
              </div>
              <div class="grid-col format-col">
                <PvBadge v-if="distribution.format" class="format-badge" :value="getDisplayFormat(distribution)"
                  :type="distribution.format.id">
                </PvBadge>
              </div>
              <div class="grid-col date-col">
                {{ formatDate(distribution.modificationDate) }}
              </div>
              <div class="grid-col actions-col">
                <div class="actions-container d-flex align-items-center justify-content-start flex-wrap">
                  <!-- Access Button -->
                  <div class="dropdown">
                    <button class="action-btns dropdown-toggle" type="button" :id="`dropdownMenuButton-${index}`"
                      data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                      {{ $t('message.datasetDetails.access') }}
                    </button>
                    <div class="dropdown-menu" :aria-labelledby="`dropdownMenuButton-${index}`">
                      <!-- Access Option -->
                      <div v-if="distribution.accessUrl && distribution.accessUrl.length > 0"
                        class="dropdown-item-wrapper">
                        <AppLink class="dropdown-item" :to="distribution.accessUrl[0]" target="_blank"
                          @click="trackAction('access', distribution)"
                          :matomo-track-download="{ format: distribution?.format?.id }"
                          @after-click="$emit('trackGoto')">
                          <span class="action-text">
                            <svg class="outgoing-icon mr-1" width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor"
                                stroke-width="2" />
                              <polyline points="15,3 21,3 21,9" stroke="currentColor" stroke-width="2" />
                              <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" stroke-width="2" />
                            </svg>
                            {{ $t('message.metadata.accessUrl') }}
                          </span>
                        </AppLink>
                        <button class="copy-btn" type="button" :title="$t('message.metadata.downloadUrl')"
                          @click="copyToClipboard(distribution.accessUrl[0])">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor"
                              stroke-width="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor"
                              stroke-width="2" />
                          </svg>
                        </button>
                      </div>

                      <!-- Download Option (if available) -->
                      <div v-if="distribution.downloadUrls && distribution.downloadUrls.length > 0"
                        class="dropdown-item-wrapper">
                        <AppLink class="dropdown-item" :to="distribution.downloadUrls[0]" target="_blank"
                          @click="trackAction('download', distribution)"
                          :matomo-track-download="{ format: distribution?.format?.id }"
                          @after-click="$emit('trackGoto')">
                          <span class="action-text">
                            <svg class="download-icon mr-1" width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor"
                                stroke-width="2" />
                              <polyline points="7,10 12,15 17,10" stroke="currentColor" stroke-width="2" />
                              <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" stroke-width="2" />
                            </svg>
                            {{ $t('message.metadata.downloadUrl') }}

                          </span>
                        </AppLink>
                        <button class="copy-btn" type="button" :title="$t('message.datasetDetails.copyUrl')"
                          @click="copyToClipboard(distribution.downloadUrls[0])">
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" stroke="currentColor"
                              stroke-width="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" stroke="currentColor"
                              stroke-width="2" />
                          </svg>
                        </button>
                      </div>

                      <span v-if="enableDownloadAs">
                        <div v-if="hasDownloadAsOptions(distribution)" class="dropdown-item-wrapper pointer">
                          <div class="dropdown-item" @click.prevent.stop="openDownloadAsModal(distribution)">

                            <svg class="download-icon mr-1" width="12" height="12" viewBox="0 0 24 24" fill="none">
                              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor"
                                stroke-width="2" />
                              <polyline points="7,10 12,15 17,10" stroke="currentColor" stroke-width="2" />
                              <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" stroke-width="2" />
                            </svg>
                            {{ $t('message.datasetDetails.datasets.modal.downloadAs') }}

                          </div>
                        </div>
                      </span>
                    </div>
                  </div>

                  <!-- Preview Button -->
                  <div v-if="showVisualisationButton && showVisualisationLink(distribution)"
                    class="action-btns preview-btn" @click="handleDisplayVisualisation(distribution)">
                    {{ $t('message.datasetDetails.preview') }}
                  </div>
                  <a v-else-if="showGeoLink(distribution)" @click="handleTrackLink(getGeoLink(distribution), 'link')"
                    :href="getGeoLink(distribution)" target="_blank" class="action-btns preview-btn">
                    {{ $t('message.datasetDetails.preview') }}
                  </a>

                </div>
              </div>
              <div v-if="useEndorsements" class="grid-col likes-col">
                <dataset-details-likes class="d-none d-lg-block" @click.stop @counts="handleEndorsementCounts"
                  resource-type="distribution" :resource-id="distribution.id" />
              </div>
            </div>

            <!-- Accordion Content -->
            <div v-if="expandedItems[distribution.id]" :id="`accordion-content-${index}`"
              :aria-labelledby="`accordion-button-${index}`" class="accordion-content" role="region">
              <div class="expanded-details">
                <dl class="details-list">
                  <dt v-if="distribution.licence && distribution.licence.label">
                    <Tooltip :title="$t('message.tooltip.datasetDetails.distributions.licence')">{{
                      $t('message.metadata.license') }}</Tooltip>
                  </dt>
                  <dd v-if="distribution.licence && distribution.licence.label">
                    <a v-if="distribution.licence.resource" :href="distribution.licence.resource">
                      {{ distribution.licence.label }}</a>
                    <span v-else>{{ distribution.licence.label }}</span>
                    <app-link
                      v-if="this.$env.content.datasetDetails?.distributions?.licencingAssistantUrl || distribution.licence?.resource"
                      :to="this.$env.content.datasetDetails?.distributions?.licencingAssistantUrl ?
                        `${this.$env.content.datasetDetails.distributions.licencingAssistantUrl}?locale=${$route.query.locale}` :
                        distribution.licence.resource" target="_blank" data-toggle="tooltip" isTooltip="true"
                      data-placement="top" :title="this.$env.content.datasetDetails?.distributions?.licencingAssistantUrl ?
                        'Licensing assistant' :
                        distribution.licence?.description"
                      @click="$emit('track-link', distribution.licence.resource, 'link')">
                      <i class="material-icons small-icon align-bottom text-dark">info</i>
                    </app-link>
                  </dd>

                  <dt>
                    <Tooltip :title="$t('message.tooltip.datasetDetails.distributions.updated')">
                      {{
                        $t('message.metadata.updated') }}</Tooltip>
                  </dt>

                  <dd><span v-if="distribution.modificationDate">{{ formatDate(distribution.modificationDate)
                  }}</span><span v-else>-</span></dd>
                  <dt>

                    <Tooltip :title="$t('message.tooltip.datasetDetails.distributions.created')">
                      {{ $t('message.metadata.created') }}</Tooltip>
                  </dt>

                  <dd><span v-if="distribution.releaseDate">{{ formatDate(distribution.releaseDate) }}</span> <span
                      v-else>-</span> </dd>

                  <dt>
                    <Tooltip :title="$t('message.dataupload.distributions.mediaType.info')">
                      {{ $t('message.metadata.mediaType') }}</Tooltip>
                  </dt>

                  <dd><span v-if="distribution.mediaType">{{ distribution.mediaType }}</span>
                    <span v-else>-</span>
                  </dd>

                  <dt v-if="distribution.downloadUrls && distribution.downloadUrls.length > 0">
                    <Tooltip :title="$t('message.datasetDetails.downloadURLTooltip')">{{
                      $t('message.metadata.downloadUrl') }}</Tooltip>
                  </dt>

                  <dd v-if="distribution.downloadUrls && distribution.downloadUrls.length > 0">
                    <AppLink :to="distribution.downloadUrls[0]"
                      :matomo-track-download="{ format: distribution?.format?.id }" @after-click="$emit('trackGoto')">{{
                        (distribution.downloadUrls[0] || '').substring(0, 80) + '...' }}
                      <svg class="outgoing-icon mr-1" width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor"
                          stroke-width="2" />
                        <polyline points="15,3 21,3 21,9" stroke="currentColor" stroke-width="2" />
                        <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" stroke-width="2" />
                      </svg>
                    </AppLink>
                  </dd>

                  <dt v-if="distribution.accessUrl && distribution.accessUrl.length > 0">
                    <Tooltip :title="$t('message.datasetDetails.accessURLTooltip')">{{ $t('message.metadata.accessUrl')
                    }}</Tooltip>
                  </dt>

                  <dd v-if="distribution.accessUrl && distribution.accessUrl.length > 0">
                    <a :href="distribution.accessUrl[0]">{{
                      (distribution.accessUrl[0] || '').substring(0, 80) + '...' }}
                      <svg class="outgoing-icon mr-1" width="12" height="12" viewBox="0 0 24 24" fill="none">
                        <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor"
                          stroke-width="2" />
                        <polyline points="15,3 21,3 21,9" stroke="currentColor" stroke-width="2" />
                        <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" stroke-width="2" />
                      </svg></a>
                  </dd>

                  <dt>
                    {{ $t('message.metadata.validate') }}
                  </dt>

                  <dd>
                    <router-link :to="`/datasets/${getID}/quality`"> {{ $t('message.metadata.validate') }}</router-link>
                  </dd>


                  <dt v-if="distribution.id">
                    {{ $t('message.metadata.linkedData') }}
                  </dt>
                  <dd v-if="distribution.id">
                    <a :href="createLinkedDataURL('rdf', distribution.id)" rel="noopener" target="_blank" class="mr-2">
                      RDF/XML
                    </a>
                    <a :href="createLinkedDataURL('ttl', distribution.id)" rel="noopener" target="_blank" class="mr-2">
                      Turtle
                    </a>
                    <a :href="createLinkedDataURL('n3', distribution.id)" rel="noopener" target="_blank" class="mr-2">
                      Notation3
                    </a>
                    <a :href="createLinkedDataURL('nt', distribution.id)" rel="noopener" target="_blank" class="mr-2">
                      N-Triples
                    </a>
                    <a :href="createLinkedDataURL('jsonld', distribution.id)" rel="noopener" target="_blank"
                      class="mr-2">
                      JSON-LD
                    </a>
                  </dd>
                  <dt>
                    <Tooltip :title="$t('message.tooltip.datasetDetails.distributions.representationTechnique')">
                      {{ $t('message.metadata.representationTechnique') }}
                    </Tooltip>
                  </dt>
                  <dd>
                    {{ distribution.representationTechnique && distribution.representationTechnique.length > 0 ?
                      getRepresentationTechniqueLabel(distribution) : '-' }}
                  </dd>
                </dl>
              </div>
            </div>
          </div>

          <!-- Empty state -->
          <div v-if="!getDistributions || getDistributions.length === 0" class="grid-row empty-state">
            <div class="grid-col-full text-center text-muted">
              No Distributions Available
            </div>
          </div>
        </div>
      </div>

      <div v-if="!showingAll" class="fade-overlay"></div>

    </div>
    <!-- Mobile Card View -->
    <div class="distributions-cards d-md-none">
      <div v-for="(distribution, index) in visibleDistributions" :key="distribution.id" class="distribution-card"
        :class="{ 'expanded': expandedItems[distribution.id] }">

        <!-- Card Header -->
        <div class="card-header" @click="toggleAccordion(distribution.id)">
          <div class="card-title-section">
            <h3 class="distribution-title">{{ getDistributionTitle(distribution) }}</h3>
            <div class="d-flex justify-content-between align-items-center">
              <div class="card-meta">
                <PvBadge v-if="distribution.format" class="format-badge" :value="distribution.format"
                  :type="distribution.format.id">
                </PvBadge>
                <span class="date">{{ formatDate(distribution.modificationDate) }}</span>
              </div>
              <dataset-details-likes class="d-none d-lg-block" v-if="useEndorsements" @click.stop
                @counts="handleEndorsementCounts" resource-type="distribution" :resource-id="distribution.id" />
            </div>
          </div>
          <svg class="accordion-arrow" :class="{ 'rotated': expandedItems[distribution.id] }" width="16" height="16"
            viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="2" fill="none" />
          </svg>
        </div>

        <!-- Card Actions -->
        <div class="card-actions">
          <div class="position-relative d-inline-block">
            <div v-if="showVisualisationButton && showVisualisationLink(distribution)"
              class="card-action-btn preview-btn" @click="handleDisplayVisualisation(distribution)">
              {{ $t('message.datasetDetails.preview') }}
            </div>
            <a v-else-if="showGeoLink(distribution)" @click="handleTrackLink(getGeoLink(distribution), 'link')"
              :href="getGeoLink(distribution)" target="_blank" class="card-action-btn preview-btn">
              {{ $t('message.datasetDetails.preview') }}
            </a>
          </div>

          <!-- Access Buttons -->
          <div class="access-buttons">
            <AppLink v-if="distribution.accessUrl && distribution.accessUrl.length > 0"
              class="card-action-btn access-btn" :to="distribution.accessUrl[0]" target="_blank"
              @click="trackAction('access', distribution)" :matomo-track-download="{ format: distribution?.format?.id }"
              @after-click="$emit('trackGoto')">
              <svg class="btn-icon" width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor"
                  stroke-width="2" />
                <polyline points="15,3 21,3 21,9" stroke="currentColor" stroke-width="2" />
                <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" stroke-width="2" />
              </svg>
              {{ $t('message.metadata.accessUrl') }}
            </AppLink>

            <AppLink v-if="distribution.downloadUrls && distribution.downloadUrls.length > 0"
              class="card-action-btn download-btn" :to="distribution.downloadUrls[0]" target="_blank"
              @click="trackAction('download', distribution)"
              :matomo-track-download="{ format: distribution?.format?.id }" @after-click="$emit('trackGoto')">
              <svg class="btn-icon" width="12" height="12" viewBox="0 0 24 24" fill="none">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" stroke-width="2" />
                <polyline points="7,10 12,15 17,10" stroke="currentColor" stroke-width="2" />
                <line x1="12" y1="15" x2="12" y2="3" stroke="currentColor" stroke-width="2" />
              </svg>
              {{ $t('message.metadata.downloadUrl') }}
            </AppLink>
          </div>
        </div>

        <!-- Expanded Content (same as before) -->
        <div v-if="expandedItems[distribution.id]" class="card-expanded-content">
          <div class="expanded-details">
            <dl class="details-list">
              <dt v-if="distribution.licence && distribution.licence.label">
                <Tooltip :title="$t('message.tooltip.datasetDetails.distributions.licence')">{{
                  $t('message.metadata.license') }}</Tooltip>
              </dt>
              <dd v-if="distribution.licence && distribution.licence.label">
                <a v-if="distribution.licence.resource" :href="distribution.licence.resource">
                  {{ distribution.licence.label }}</a>
                <span v-else>{{ distribution.licence.label }}</span>
                <app-link
                  v-if="this.$env.content.datasetDetails?.distributions?.licencingAssistantUrl || distribution.licence?.resource"
                  :to="this.$env.content.datasetDetails?.distributions?.licencingAssistantUrl ?
                    `${this.$env.content.datasetDetails.distributions.licencingAssistantUrl}?locale=${$route.query.locale}` :
                    distribution.licence.resource" target="_blank" data-toggle="tooltip" isTooltip="true"
                  data-placement="top" :title="this.$env.content.datasetDetails?.distributions?.licencingAssistantUrl ?
                    'Licensing assistant' :
                    distribution.licence?.description"
                  @click="$emit('track-link', distribution.licence.resource, 'link')">
                  <i class="material-icons small-icon align-bottom text-dark">info</i>
                </app-link>
              </dd>

              <dt>
                <Tooltip :title="$t('message.tooltip.datasetDetails.distributions.updated')">
                  {{
                    $t('message.metadata.updated') }}</Tooltip>
              </dt>

              <dd><span v-if="distribution.modificationDate">{{ formatDate(distribution.modificationDate)
              }}</span><span v-else>-</span></dd>
              <dt>

                <Tooltip :title="$t('message.tooltip.datasetDetails.distributions.created')">
                  {{ $t('message.metadata.created') }}</Tooltip>
              </dt>

              <dd><span v-if="distribution.releaseDate">{{ formatDate(distribution.releaseDate) }}</span> <span
                  v-else>-</span> </dd>

              <dt>
                <Tooltip :title="$t('message.dataupload.distributions.mediaType.info')">
                  {{ $t('message.metadata.mediaType') }}</Tooltip>
              </dt>

              <dd><span v-if="distribution.mediaType">{{ distribution.mediaType }}</span>
                <span v-else>-</span>
              </dd>

              <dt v-if="distribution.downloadUrls && distribution.downloadUrls.length > 0">
                <Tooltip :title="$t('message.datasetDetails.downloadURLTooltip')">{{
                  $t('message.metadata.downloadUrl') }}</Tooltip>
              </dt>

              <dd v-if="distribution.downloadUrls && distribution.downloadUrls.length > 0">
                <a :href="distribution.downloadUrls[0]">{{
                  (distribution.downloadUrls[0] || '').substring(0, 80) + '...' }}
                  <svg class="outgoing-icon mr-1" width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor"
                      stroke-width="2" />
                    <polyline points="15,3 21,3 21,9" stroke="currentColor" stroke-width="2" />
                    <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" stroke-width="2" />
                  </svg>
                </a>
              </dd>

              <dt v-if="distribution.accessUrl && distribution.accessUrl.length > 0">
                <Tooltip :title="$t('message.datasetDetails.accessURLTooltip')">{{ $t('message.metadata.accessUrl')
                }}</Tooltip>
              </dt>

              <dd v-if="distribution.accessUrl && distribution.accessUrl.length > 0">
                <a :href="distribution.accessUrl[0]">{{
                  (distribution.accessUrl[0] || '').substring(0, 80) + '...' }}
                  <svg class="outgoing-icon mr-1" width="12" height="12" viewBox="0 0 24 24" fill="none">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor"
                      stroke-width="2" />
                    <polyline points="15,3 21,3 21,9" stroke="currentColor" stroke-width="2" />
                    <line x1="10" y1="14" x2="21" y2="3" stroke="currentColor" stroke-width="2" />
                  </svg></a>
              </dd>

              <dt>
                {{ $t('message.metadata.validate') }}
              </dt>

              <dd>
                <router-link :to="`/datasets/${getID}/quality`"> {{ $t('message.metadata.validate') }}</router-link>
              </dd>

              <dt v-if="distribution.id">
                {{ $t('message.metadata.linkedData') }}
              </dt>
              <dd v-if="distribution.id">
                <a :href="createLinkedDataURL('rdf', distribution.id)" rel="noopener" target="_blank" class="mr-2">
                  RDF/XML
                </a>
                <a :href="createLinkedDataURL('ttl', distribution.id)" rel="noopener" target="_blank" class="mr-2">
                  Turtle
                </a>
                <a :href="createLinkedDataURL('n3', distribution.id)" rel="noopener" target="_blank" class="mr-2">
                  Notation3
                </a>
                <a :href="createLinkedDataURL('nt', distribution.id)" rel="noopener" target="_blank" class="mr-2">
                  N-Triples
                </a>
                <a :href="createLinkedDataURL('jsonld', distribution.id)" rel="noopener" target="_blank" class="mr-2">
                  JSON-LD
                </a>
              </dd>
            </dl>
          </div>
        </div>
      </div>

      <!-- Empty state for mobile -->
      <div v-if="!getDistributions || getDistributions.length === 0" class="empty-state-card">
        <div class="text-center text-muted">
          No Distributions Available
        </div>
      </div>
    </div>

    <!-- Show More / Show All / Show Less Buttons -->
    <div v-if="hasMoreDistributions || showingMoreThanInitial" class="show-all-container">
      <button v-if="!showingAll && hasMoreDistributions" class="show-more-btn" @click="showMoreDistributions">
        <svg class="arrow-icon" width="16" height="16" viewBox="0 0 16 16">
          <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="2" fill="none" />
        </svg>
        Show 10 more
      </button>


      <button v-if="showingAll || showingMoreThanInitial" class="show-more-btn" :class="{ 'ml-2': !showingAll }"
        @click="resetView">
        <svg class="arrow-icon rotated" width="16" height="16" viewBox="0 0 16 16">
          <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="2" fill="none" />
        </svg>
        {{ $t('message.metadata.showLess') }}
      </button>

      <button v-if="!showingAll && hasMoreDistributions" class="show-all-btn ml-2" @click="showAllDistributions">
        <svg class="arrow-icon" width="16" height="16" viewBox="0 0 16 16">
          <path d="M4 6l4 4 4-4" stroke="currentColor" stroke-width="2" fill="none" />
        </svg>
        {{ $t('message.metadata.showAll') }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import {
  helpers,
  Tooltip,
  PvBadge,
  AppLink
} from "@piveau/piveau-hub-ui-modules";
import DatasetDetailsLikes from "./datasetDetailsCommentsAndLikes/DatasetDetailsLikes.vue";
import { has, isNil, sortBy } from "lodash-es";
import App from "@/App.vue";


const { getTranslationFor, getDistributionTitle } = helpers;

export default {
  name: "ECDistributionsHeader",
  data: function () {
    return {
      useEndorsements: this.$env.content.datasetDetails.showRatingService,
      enableDownloadAs: this.$env.content.datasetDetails.downloadAs.enable,
      expandedItems: {},
      visibleCount: 8,
      incrementSize: 10,
      initialVisibleCount: 8,
      sortBy: 'title',
      sortOrder: 'asc',
      showVisualisationButton: this.$env.content.datasetDetails.distributions?.showVisualisation,
      visualisationLinkFormats: [
        'pdf',
        'csv',
        'tsv',
        'ods',
        'xlsx',
        'xls',
        'pdf',
      ],
      geoLinkFormats: {
        wms: 'WMS',
        'application/vnd.ogc.wms_xml': 'WMS',
        'application/vnd.ogc.wms+xml': 'WMS',
        'application/x-ogc-wms': 'WMS',
        'ogc:wms': 'WMS',
        geojson: 'GeoJSON',
        fiware_cb: 'fiware_cb',
        'fiware-cb': 'fiware_cb',
        ogcfeat: 'OGCFEAT',
        'ogc api': 'OGCFEAT',
        'ogc api features': 'OGCFEAT',
        'ogc api - features': 'OGCFEAT'
      },
      geoLink: this.$env?.datasetDetails?.distributions?.geoLink,
      conversionFormats: this.$env.content.datasetDetails.downloadAs?.conversionFormats || [],
      supportedDownloadAsSourceFormats: [
        'csv',
        'tsv',
        'xls',
        'xlsx',
        'xlsm',
        'ods',
        'json',
        'jsonl',
        'html',
      ],
      endorsementCounts: {}, // Store endorsement counts indexed by distribution ID
    };
  },
  components: {
    Tooltip,
    PvBadge,
    AppLink,
    DatasetDetailsLikes,
  },
  props: {
  },
  computed: {
    ...mapGetters('datasetDetails', [
      'getDistributions',
      'getID',
      'getCatalog'
    ]),
    sortedAndFilteredDistributions() {
      if (!this.getDistributions) return [];
      let distributions = [...this.getDistributions];

      if (this.sortOrder) {
        distributions.sort((a, b) => {
          let valueA, valueB;

          // Sortierung nach gewähltem Kriterium
          if (this.sortBy === 'title') {
            valueA = this.getDistributionTitle(a).toLowerCase();
            valueB = this.getDistributionTitle(b).toLowerCase();
            return this.sortOrder === 'asc'
              ? valueA.localeCompare(valueB)
              : valueB.localeCompare(valueA);
          }
          else if (this.sortBy === 'format') {
            valueA = (a.format?.label || a.format?.id || '').toLowerCase();
            valueB = (b.format?.label || b.format?.id || '').toLowerCase();
            return this.sortOrder === 'asc'
              ? valueA.localeCompare(valueB)
              : valueB.localeCompare(valueA);
          }
          else if (this.sortBy === 'updated') {
            valueA = a.modificationDate ? new Date(a.modificationDate).getTime() : 0;
            valueB = b.modificationDate ? new Date(b.modificationDate).getTime() : 0;
            return this.sortOrder === 'asc'
              ? valueA - valueB
              : valueB - valueA;
          }
          else if (this.sortBy === 'likes') {
            valueA = this.endorsementCounts[a.id]?.likes || 0;
            valueB = this.endorsementCounts[b.id]?.likes || 0;
            return this.sortOrder === 'asc'
              ? valueA - valueB
              : valueB - valueA;
          }

          return 0;
        });
      }

      return distributions;
    },
    visibleDistributions() {
      if (!this.getDistributions) return [];
      return this.sortedAndFilteredDistributions.slice(0, this.visibleCount);
    },
    showingMoreThanInitial() {
      return this.visibleCount > this.initialVisibleCount;
    },
    hasMoreDistributions() {
      return this.sortedAndFilteredDistributions && this.sortedAndFilteredDistributions.length > this.initialVisibleCount;
    },
    showingAll() {
      return this.visibleCount >= this.sortedAndFilteredDistributions?.length;
    },
    remainingCount() {
      if (!this.sortedAndFilteredDistributions) return 0;
      const remaining = this.sortedAndFilteredDistributions.length - this.visibleCount;
      return Math.min(remaining, this.incrementSize);
    }
  },
  methods: {
    ...mapActions('datasetDetails', [
      'selectDistributionForDownloadAs',
    ]),
    getNormalizedGeoFormat(distribution) {
      const normalize = (value) => String(value || '')
        .trim()
        .toLowerCase()
        .replace(/\s+/g, ' ');

      const candidates = [
        distribution?.format?.label,
        distribution?.format?.id,
        distribution?.mediaType,
      ]
        .map(normalize)
        .filter(Boolean);

      for (const candidate of candidates) {
        const mapped = this.geoLinkFormats[candidate];
        if (mapped) return mapped;

        if (
          candidate === 'wms'
          || candidate.includes('ogc.wms')
          || candidate.includes('service=wms')
          || candidate.includes('request=getmap')
        ) {
          return 'WMS';
        }
      }

      return null;
    },

    getDisplayFormat(distribution) {
      const format = distribution?.format;
      if (!format) return format;

      if (this.getNormalizedGeoFormat(distribution) === 'OGCFEAT') {
        return {
          ...format,
          label: 'OGC API Features'
        };
      }

      return format;
    },

    showGeoLink(distribution) {
      if (!distribution?.id || !distribution?.accessUrl?.[0]) return false;
      return Boolean(this.getNormalizedGeoFormat(distribution));
    },
  
  showVisualisationLink(distribution) {
    if (!distribution?.format?.label ||
        (!distribution?.downloadUrls?.[0] && !distribution?.accessUrl?.[0])) return false;
      const f = distribution?.format?.id?.toLowerCase();
      return f && this.visualisationLinkFormats.includes(f);
    },

    getGeoLink(distribution) {
      const f = this.getNormalizedGeoFormat(distribution);
      if (!f) return null;

      if (this.geoLink) {
        const geoLinkVariables = {
          catalog: this.getCatalog.id,
          dataset: this.getID,
          distribution: distribution.id,
          type: f,
          lang: this.$route.query.locale,
          accessUrl: distribution?.accessUrl?.[0],
        }
        // Inject variables into geo link
        let processedGeoLink = this.geoLink;
        for (let linkVariable in geoLinkVariables) {
          processedGeoLink = processedGeoLink.replace(`{${linkVariable}}`, geoLinkVariables[linkVariable]);
        }
        return processedGeoLink;
      }
      // Return default Geo Visualisation Link if no link in user-config provided
      return `/geo-viewer/?catalog=${this.getCatalog.id}&dataset=${this.getID}&distribution=${distribution.id}&type=${f}&lang=${this.$route.query.locale}`;
    },

    getDownloadUrl(distribution) {
      return distribution?.downloadUrls?.[0] || distribution?.accessUrl?.[0] || null;
    },

    getDownloadAsOptions(distribution) {
      const distributionFormat = distribution?.format?.id;
      if (!distributionFormat) return [];

      const match = this.conversionFormats.find((conversionFormat) => (
        conversionFormat?.sourceFileFormat?.toLowerCase() === distributionFormat.toLowerCase()
      ));

      return match?.targetFileFormat || [];
    },

    isSupportedDownloadAsSourceFormat(distribution) {
      const sourceFormat = String(distribution?.format?.id || '').trim().toLowerCase();
      return this.supportedDownloadAsSourceFormats.includes(sourceFormat);
    },

    hasDownloadAsOptions(distribution) {
      return Boolean(
        this.getDownloadUrl(distribution)
        && this.isSupportedDownloadAsSourceFormat(distribution)
      );
    },

    openDownloadAsModal(distribution) {
      const selectOptions = this.getDownloadAsOptions(distribution);
      this.selectDistributionForDownloadAs({ distribution, selectOptions });

      // Open Bootstrap modal explicitly for reliability in dropdown context.
      const modalElement = document.getElementById('downloadAsModal');
      if (!modalElement) return;

      if (window.$ && typeof window.$(modalElement).modal === 'function') {
        window.$(modalElement).modal('show');
        return;
      }

      modalElement.classList.add('show');
      modalElement.style.display = 'block';
      modalElement.setAttribute('aria-modal', 'true');
      modalElement.removeAttribute('aria-hidden');
    },

    handleDisplayVisualisation(distribution) {
      const downloadUrl = this.getDownloadUrl(distribution);
      console.log('Displaying visualisation for:', distribution, 'URL:', downloadUrl);
      this.$emit('displayVisualisation', downloadUrl);
    },

    handleTrackLink(url, type) {
      console.log('Tracking link:', url, type);
      this.$emit('track-link', url, type);
    },
    toggleSort(sortBy) {
      if (this.sortBy === sortBy) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortBy = sortBy;
        this.sortOrder = 'asc';
      }
      this.expandedItems = {};
    },
    handleEndorsementCounts({ id, likes, dislikes }) {
      // update reactive object so sorting can use it
      this.endorsementCounts = {
        ...this.endorsementCounts,
        [id]: { likes, dislikes }
      };
    },
    getDistributionTitle(distribution) {
      return distribution.title
        ? getTranslationFor(distribution.title, this.$route.query.locale, this.getLanguages)
        : this.getID || '-';
    },
    createLinkedDataURL(format, id) {
      return `${this.$env.api.hubUrl}distributions/${id}.${format}`;
    },
    showMoreDistributions() {
      this.visibleCount = Math.min(
        this.visibleCount + this.incrementSize,
        this.sortedAndFilteredDistributions.length
      );
    },
    showAllDistributions() {
      this.visibleCount = this.sortedAndFilteredDistributions.length;
    },
    resetView() {
      this.visibleCount = this.initialVisibleCount;
      // Scroll to top of distributions
      this.$el.querySelector('.distributions-grid')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    },
    formatDate(dateString) {
      if (!dateString) return '-';

      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        return date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        });
      } catch (error) {
        console.warn('Error formatting date:', dateString, error);
        return dateString;
      }
    },
    copyToClipboard(url) {
      if (!url) return;

      navigator.clipboard.writeText(url).then(() => {
        console.log('URL copied to clipboard:', url);
        this.showCopyFeedback();
      }).catch(err => {
        console.error('Failed to copy URL:', err);
        this.fallbackCopyToClipboard(url);
      });
    },
    fallbackCopyToClipboard(text) {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand('copy');
        this.showCopyFeedback();
      } catch (err) {
        console.error('Fallback: Unable to copy', err);
      }
      document.body.removeChild(textArea);
    },
    showCopyFeedback() {
      console.log('URL copied successfully!');
    },
    trackAction(actionType, distribution) {
      console.log(`User ${actionType}ed distribution:`, distribution);
    },
    downloadDistribution(distribution) {
      if (distribution.downloadUrls) {
        window.open(distribution.downloadUrls, '_blank');
      }
    },
    viewDistribution(distribution) {
      if (distribution.accessUrl) {
        window.open(distribution.accessUrl, '_blank');
      }
    },
    initTooltips() {
      if (typeof $ !== 'undefined') {
        $('.tooltip').remove();
        $('[data-toggle="tooltip"]').tooltip('dispose');

        this.$nextTick(() => {
          $('[data-toggle="tooltip"]').tooltip({
            container: 'body',
            html: true
          });
        });
      }
    },
    toggleAccordion(distributionId) {
      const newExpandedItems = { ...this.expandedItems };
      if (newExpandedItems[distributionId]) {
        delete newExpandedItems[distributionId];
      } else {
        newExpandedItems[distributionId] = true;
      }
      this.expandedItems = newExpandedItems;

      this.$nextTick(() => {
        this.initTooltips();
      });
    },
    getRepresentationTechniqueLabel(distribution) {
      const items = distribution?.representationTechnique;
      console.log(items);
      if (!items || !items.length) return '-';

      return items
        .map((item) => getTranslationFor(item.label, this.$route.query.locale, this.getLanguages) || item.id)
        .join(', ');
    },
  },
  mounted() {
    console.log('getDistributions (raw from store):', JSON.stringify(this.getDistributions, null, 2));
    this.initTooltips();
  },
  updated() {
    this.initTooltips();
  },
  beforeUnmount() {
    if (typeof $ !== 'undefined') {
      $('.tooltip').remove();
      $('[data-toggle="tooltip"]').tooltip('dispose');
    }
  }
}
</script>

<style lang="scss" scoped>
.actions-container {
  gap: 15px;

  .preview-btn {
    flex-shrink: 0;
  }

  .dropdown {
    flex-shrink: 0;
    margin-left: auto;
  }
}


.sort-button {
  background: none;
  border: none;
  display: flex;
  align-items: center;
  gap: 0.2rem;
  font-weight: bold;
  cursor: pointer;
  padding: 0;
  color: inherit;
  font-size: inherit;
  font-family: inherit;

  &:hover {
    color: #0E47CB;

    .sort-arrows .sort-arrow {
      opacity: 0.7;
    }
  }

}

.sort-arrows {
  display: flex;
  flex-direction: column;
  gap: 1px;
  margin-left: 0.25rem;
}

.sort-arrow {
  color: #3b3b3b;
  opacity: 0.8;
  transition: all 0.2s ease;

  &.active {
    color: #0E47CB;
    opacity: 1;
  }

  &:not(.active):hover {
    opacity: 0.7;
  }
}

.sort-arrow-up {
  margin-bottom: -1px;
}

.sort-arrow-down {
  margin-top: -1px;
}

.dsd-item {
  margin-bottom: 0rem;
}

.distributions-wrapper {
  position: relative;
}

.show-all-container {
  display: flex;
  justify-content: left;
  gap: 0.5rem;
  padding: 1.5rem 0 0rem;
}

.show-more-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #fff;
  border: 2px solid #0e47cb;
  color: #0e47cb;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-weight: 400;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #3e6cd5;
    border-color: #3e6cd5;
    color: #fff;

    .arrow-icon {
      color: #fff;
    }

  }

  .arrow-icon {
    color: #0E47CB;
    transition: transform 0.3s ease;

    &.rotated {
      transform: rotate(180deg);
    }
  }
}

.show-all-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #0e47cb;
  border: 1px solid #0e47cb;
  color: #fff;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-weight: 400;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #3e6cd5;
    border-color: #3e6cd5;

    .arrow-icon {
      color: #fff;
    }
  }

  .arrow-icon {
    color: #fff;
    transition: transform 0.3s ease;

    &.rotated {
      transform: rotate(180deg);
    }
  }
}

.fade-overlay {
  position: absolute;
  bottom: 70px;
  left: 0;
  right: 0;
  height: 45px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 0.95) 50%, rgba(255, 255, 255, 1) 100%);
  pointer-events: none;
  z-index: 1;
}

.accordion-title {
  background: none;
  border: none;
  display: contents;
  cursor: pointer;
}

.action-btns {
  font-family: Arial;
  font-weight: 400;
  font-size: 16px !important;
  line-height: 24px;
  letter-spacing: 0px;
  color: #0E47CB;
  background: none;
  border: none;
  cursor: pointer;

  &:hover {
    text-decoration: none;
  }
}

.dropdown {
  position: relative;
  display: inline-block;
}

.dropdown-item-wrapper {
  display: flex;
  align-items: center;
  padding: 0;

  .dropdown-item {
    flex: 1;
    padding: 0.475rem 0.75rem;
    margin: 0;
    border: none;
    color: #0E47CB;

    .action-text {
      align-items: center;
      justify-content: space-between;
    }

    .outgoing-icon,
    .download-icon {
      color: #0E47CB;
      flex-shrink: 0;
    }

    &:hover {
      background-color: #0E47CB;
      text-decoration: none;
      color: #fff;
      border-radius: 4px;

      .outgoing-icon,
      .download-icon {
        color: #ffffff;
      }
    }

    &:focus {
      background-color: transparent !important;
      text-decoration: none;
      color: #0E47CB !important;
      outline: none;

      svg {
        color: #0E47CB !important;
      }

      .outgoing-icon,
      .download-icon {
        color: #0E47CB;
      }
    }

    &:active {
      background-color: #0E47CB;
      color: #fff;

      .outgoing-icon,
      .download-icon {
        color: #ffffff;
      }
    }
  }

  .copy-btn {
    background: none;
    border: none;
    padding: 0.375rem 0.9rem;
    cursor: pointer;
    color: #0E47CB;
    transition: color 0.2s ease;
    border-left: 0.02rem solid #d2e0ff;

    &:hover {
      color: #fff;
      background-color: #0E47CB;
      border-radius: 4px;
    }


  }
}

.dropdown-menu {
  min-width: 200px;
  padding: 0;
  border: 1px solid #dee2e6;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  left: 0;
  right: auto;
  top: calc(100% + 0.25rem);
  z-index: 1050;

  &.show {
    transform: none !important;
  }

  .dropdown-item-wrapper+.dropdown-item-wrapper {
    border-top: 1px solid #dee2e6;
  }
}

.actions-col {
  .dropdown-toggle {

    &::after {
      margin-left: 0.5rem;
    }
  }

  &:hover {
    text-decoration: none;
  }
}

.format-badge {
  font-size: 12px !important;
}

.table-responsive-wrapper {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  width: 100%;

  @media (min-width: 992px) {
    overflow-x: visible;
  }
}

.distribution-title {
  font-family: Arial;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0px;
  color: #0E47CB;
  white-space: nowrap;
  text-align: left;
}

.title-with-arrow {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  text-align: left;
}

.accordion-arrow {
  color: #0E47CB;
  transition: transform 0.3s ease;
  flex-shrink: 0;

  &.rotated {
    transform: rotate(180deg);
  }
}

.distribution-header {
  display: contents;
  background: none;
  border: none;
  font-family: inherit;
  text-align: left;
  width: 100%;

  &:hover .grid-col {
    background-color: #f8f9fa !important;
  }

  &:focus {
    outline: none;

    .grid-col {
      outline: 2px solid #0E47CB;
      outline-offset: -2px;
    }
  }
}

.distribution-item {
  display: contents;

  &.accordion-open .grid-col {
    border-bottom: none !important;
  }
}

.accordion-content {
  grid-column: 1 / -1;
  // background-color: #f8f9fa;
  // border-left: 3px solid #0E47CB;
  padding: 1rem;
  border-bottom: 1px solid #dee2e6;
  animation: slideDown 0.2s ease-out;

  .expanded-details {
    width: 100%;

    h4 {
      margin-bottom: 1rem;
      color: #0E47CB;
      font-size: 1.1rem;
    }

    .details-list {
      display: grid;
      grid-template-columns: auto 1fr;
      gap: 0px 0.8rem;
      margin: 0;
      font-size: 12px;

      dt {
        font-weight: 600;
        color: #333;
      }

      dd {
        margin: 0;
        word-break: break-all;
      }
    }
  }
}

@keyframes slideDown {
  from {
    opacity: 0;
    max-height: 0;
  }

  to {
    opacity: 1;
    max-height: 200px;
  }
}

.distributions-grid {
  display: grid;
  gap: 0;
  grid-template-columns: minmax(200px, 1fr) 105px 80px minmax(120px, auto);
  min-width: 590px;
  width: max-content;

  @media (min-width: 992px) {
    grid-template-columns: calc(55% - 15px) calc(10% + 15px) 15% 20%;
    min-width: auto;
    width: 100%;
  }

  &.has-rating {
    grid-template-columns: minmax(180px, 1fr) 75px 80px minmax(100px, auto) 80px;
    min-width: 590px;

    @media (min-width: 992px) {
      grid-template-columns: calc(45% - 15px) calc(10% + 15px) 15% 20% 10%;
    }
  }

  .grid-header {
    display: contents;

    .grid-col {
      background-color: #F3F6FC;
      font-weight: bold;
      padding: 0.75rem;
      white-space: nowrap;
      border-bottom: 2px solid #dee2e6;
      display: flex;
      align-items: center;

      &:last-child {
        border-right: none;
      }
    }
  }

  .grid-row {
    display: contents;

    .grid-col {
      padding: 0.75rem;
      border-bottom: 1px solid #dee2e6;
      display: flex;
      align-items: center;
      white-space: nowrap;
      background-color: white;

      &:last-child {
        border-right: none;
      }

      @media (min-width: 992px) {
        white-space: normal;
      }
    }
  }

  .grid-col-full {
    grid-column: 1 / -1;
    padding: 2rem;
    border-bottom: 1px solid #dee2e6;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
  }
}

@media (min-width: 992px) {
  .distribution-title {
    white-space: normal;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.badge {
  font-size: 0.875rem;
}

.btn-group .btn {
  margin-right: 0.25rem;

  &:last-child {
    margin-right: 0;
  }
}

@media (max-width: 767.98px) {
  .distributions-grid {
    font-size: 0.875rem;

    .btn-group .btn {
      padding: 0.25rem 0.5rem;
      font-size: 0.75rem;
    }
  }

  .dropdown-menu {
    min-width: 160px;
  }

  .dropdown-item {
    font-size: 0.875rem;
    padding: 0.25rem 0.5rem;
  }

  .copy-btn {
    padding: 0.25rem 0.375rem;
  }
}

/* Mobile Card Styles */
.mobile-sort-controls {
  padding: 0.75rem;
  background-color: #F3F6FC;
  border-radius: 4px;
  margin-top: 10px;

  .sort-button {
    background: none;
    border: none;
    display: flex;
    align-items: center;
    gap: 0.2rem;
    font-weight: bold;
    cursor: pointer;
    padding: 0;
    color: inherit;
    font-size: inherit;
    font-family: inherit;
  }
}

.distributions-cards {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.distribution-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  transition: box-shadow 0.2s ease;
  margin-bottom: 20px;

  &.expanded {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  .distribution-title {
    font-family: Arial;
    font-weight: 700;
    font-size: 16px;
    line-height: 24px;
    color: #0E47CB;
    margin: 0 0 0.5rem 0;
    word-wrap: break-word;

    // Truncate after 3 lines for mobile cards
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }


}

.card-header {
  padding: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  cursor: pointer;
  border-bottom: 1px solid #f0f0f0;
  background-color: #F3F6FC;

  &:hover {
    background-color: #F3F6FC;
  }
}

.card-title-section {
  flex: 1;
  min-width: 0;
}

.distribution-title {
  font-family: Arial;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: #0E47CB;
  margin: 0 0 0.5rem 0;
  word-wrap: break-word;

  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: normal;


}

.card-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;

  .format-badge {
    font-size: 12px !important;
  }

  .date {
    font-size: 14px;
    color: #6c757d;
  }
}

.accordion-arrow {
  color: #0E47CB;
  transition: transform 0.3s ease;
  flex-shrink: 0;
  margin-left: 0.5rem;

  &.rotated {
    transform: rotate(180deg);
  }
}

.card-actions {
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.access-buttons {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.card-action-btn {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  transition: all 0.2s ease;
  border: 1px solid #0E47CB;
  text-align: center;
  justify-content: center;

  .btn-icon {
    flex-shrink: 0;
  }

  &.preview-btn {
    background-color: white;
    color: #0E47CB;

    &:hover {
      background-color: #0a3aa0;
      text-decoration: none;
      color: white;
    }
  }

  &.access-btn,
  &.download-btn {
    background-color: white;
    color: #0E47CB;

    &:hover {
      background-color: #0E47CB;
      color: white;
      text-decoration: none;

      .btn-icon {
        color: white;
      }
    }

    .btn-icon {
      color: #0E47CB;
    }
  }
}

.card-expanded-content {
  padding: 1rem;
  background-color: #F3F6FC;
  border-top: 1px solid #dee2e6;

  .details-list {
    display: grid;
    grid-template-columns: auto 1fr;
    gap: 0.5rem 1rem;
    margin: 0;
    font-size: 14px;

    dt {
      font-weight: 600;
      color: #333;
    }

    dd {
      margin: 0;
      word-break: break-word;
    }
  }
}

.empty-state-card {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 2rem;
  text-align: center;
}

/* Responsive adjustments */
@media (max-width: 576px) {
  .distribution-title {
    font-size: 14px;
    line-height: 20px;
  }

  .card-header {
    padding: 0.75rem;
  }

  .card-actions {
    padding: 0.75rem;
  }

  .card-action-btn {
    padding: 0.625rem 0.875rem;
    font-size: 14px;
  }
}
</style>